import { BROADCAST_URL, RTMP_SERVER, studioApi } from "../../api";
import useClipboard from "../../utils";
import useBucketStore from "../../store";
import { IVideo } from "../../interfaces";
import { ICourse } from "../../screens/list";
import moment from "moment";

export type IPLayer = "old" | "new";
export type IURL = { label: string; value: string };

interface IProps {
  player: "old" | "new";
  setPlayer: (player: "old" | "new") => void;
  latest: IVideo;
  course: ICourse;
  playerUrls: IURL[];
  onChangeUrl: any;
  activeUrl: IURL;
}

export default function YoutubeVideoDetails({
  player,
  setPlayer,
  latest,
  course,
  playerUrls,
  activeUrl,
  onChangeUrl,
}: IProps) {
  const { copy } = useClipboard();
  const { buckets } = useBucketStore();

  const handleChangeBucket = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const bucket = e.target.value;
    if (latest.status === "rendering" || latest.status === "rendered") {
      return alert("Sorry, you can't change the bucket because the class is rendering.");
    }
    try {
      await studioApi.put(`videos/${latest._id}`, { bucket });
      alert("Bucket updated successfully.");
    } catch (e: any) {
      alert(e.message);
    }
  };

  if (!latest._id || !activeUrl?.value) return <div />;

  const getVisibilityClass = (visibility: string) => {
    if (visibility === "public") return "text-emerald-600 bg-emerald-50 border-emerald-200";
    if (visibility === "private") return "text-rose-600 bg-rose-50 border-rose-200";
    return "text-amber-600 bg-amber-50 border-amber-200";
  };

  return (
    <div className="w-full  border mt-1 border-slate-200 bg-white shadow-sm overflow-hidden text-sm text-slate-700">
   
      <div className="divide-y divide-slate-100">
        <div className="grid grid-cols-4 items-center px-4 py-2.5">
          <div className=" text-blue-900 font-semibold col-span-1">Title</div>
          <div className="col-span-3  text-slate-900">
           {latest.title}
          </div>
        </div>
        <div className="grid grid-cols-4 items-center px-4 py-2.5">
          <div className="font-semibold text-blue-800 col-span-1">Scheduled At</div>
          <div className="col-span-3 font-mono text-slate-900">
            {moment(latest.schedule_at).format("DD-MM-YY HH:mm:ss")}
          </div>
        </div>
        <div className="grid grid-cols-4 items-center px-4 py-2.5">
          <div className="text-blue-900 font-semibold col-span-1">Course</div>
          <div className="col-span-3 font-medium text-slate-900">{course?.title}</div>
        </div>
        <div className="grid grid-cols-4 items-center px-4 py-2.5">
          <div className="text-blue-900 font-semibold col-span-1">Stream Key</div>
          <div className="col-span-3 flex items-center justify-between gap-4">
            <span className=" bg-green-100 border border-green-500 rounded-md px-3 py-0.5 text-xs select-all truncate">
              {latest.stream_key}
            </span>
            <button 
              onClick={() => copy(latest.stream_key, "Stream key copied")}
              className="shrink-0 px-2.5 py-1 text-xs font-medium border border-slate-200 rounded hover:bg-slate-50 active:bg-slate-100 transition cursor-pointer"
            >
              Copy
            </button>
          </div>
        </div>
        <div className="grid grid-cols-4 items-center px-4 py-2.5">
          <div className="text-blue-900 font-semibold col-span-1">Broadcast URL</div>
          <div className="col-span-3 flex items-center justify-between gap-4">
            <span className="font-mono text-xs text-slate-600 truncate">{BROADCAST_URL}</span>
            <button 
              onClick={() => copy(BROADCAST_URL, "Broadcast key copied")}
              className="shrink-0 px-2.5 py-1 text-xs font-medium border border-slate-200 rounded hover:bg-slate-50 active:bg-slate-100 transition cursor-pointer"
            >
              Copy
            </button>
          </div>
        </div>
        <div className="grid grid-cols-4 items-center px-4 py-2.5">
          <div className="text-blue-900 font-semibold col-span-1">Stream URL</div>
          <div className="col-span-3 flex items-center justify-between gap-4">
            <span className="font-mono text-xs text-slate-600 truncate">
              {latest?.videoType === "youtube" ? latest?.liveUrl : `${RTMP_SERVER}/hls/${latest.stream_key}.m3u8`}
            </span>
            <button 
              onClick={() => copy(`${RTMP_SERVER}/hls/${latest.stream_key}.m3u8`, "Stream Url copied")}
              className="shrink-0 px-2.5 py-1 text-xs font-medium border border-slate-200 rounded hover:bg-slate-50 active:bg-slate-100 transition cursor-pointer"
            >
              Copy
            </button>
          </div>
        </div>
        {latest?.videoType !== "youtube" && (
          <div className="grid grid-cols-4 items-center px-4 py-2.5">
            <div className="text-blue-900 font-semibold col-span-1">Selected Bucket</div>
            <div className="col-span-3">
              <select 
                defaultValue={latest.bucket} 
                disabled={latest.status !== "schedule"} 
                onChange={handleChangeBucket}
                className="w-full max-w-xs rounded border border-slate-200 px-2 py-1 text-xs bg-white focus:outline-none focus:border-indigo-500 disabled:bg-slate-50 disabled:text-slate-400"
              >
                <option value="">Select where to store</option>
                {buckets?.map((val) => (
                  <option key={val?._id} value={val?._id}>
                    {val?.title}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
        {latest?.videoType === "youtube" && (
          <>
            <div className="grid grid-cols-4 items-center px-4 py-2.5">
              <div className="text-blue-900 font-semibold col-span-1">YouTube Video ID</div>
              <div className="col-span-3 font-mono text-slate-900">{latest.youtube_id}</div>
            </div>

            <div className="grid grid-cols-4 items-center px-4 py-2.5">
              <div className="text-blue-900 font-semibold col-span-1">Visibility</div>
              <div className="col-span-3">
                <span className={`inline-block px-2 py-0.5 text-xs font-semibold tracking-wide rounded border uppercase ${getVisibilityClass(latest.visibility)}`}>
                  {latest.visibility}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-4 items-center px-4 py-2.5">
              <div className="text-blue-900 font-semibold col-span-1">Channel ID</div>
              <div className="col-span-3 flex items-center justify-between gap-4">
                <span className="font-mono text-xs text-slate-600 truncate">{latest.youtube_channel_id}</span>
                <button 
                  onClick={() => copy(latest.youtube_channel_id, "YouTube Channel ID copied")}
                  className="shrink-0 px-2.5 py-1 text-xs font-medium border border-slate-200 rounded hover:bg-slate-50 active:bg-slate-100 transition cursor-pointer"
                >
                  Copy
                </button>
              </div>
            </div>
            <div className="grid grid-cols-4 items-center px-4 py-2.5">
              <div className="text-blue-900 font-semibold col-span-1">YT Stream Key</div>
              <div className="col-span-3 flex items-center justify-between gap-4">
                <span className="bg-red-100 border border-red-400 rounded-md px-3 py-0.5 text-xs select-all truncate">
                  {latest.youtube_stream_key}
                </span>
                <button 
                  onClick={() => copy(latest.youtube_stream_key, "YouTube Stream Key copied")}
                  className="shrink-0 px-2.5 py-1 text-xs font-medium border border-slate-200 rounded hover:bg-slate-50 active:bg-slate-100 transition cursor-pointer"
                >
                  Copy
                </button>
              </div>
            </div>
            {latest?.cdnUrl && (
              <div className="grid grid-cols-4 items-center px-4 py-2.5">
                <div className="text-blue-900 font-semibold col-span-1">YouTube CDN URL</div>
                <div className="col-span-3 flex items-center justify-between gap-4">
                  <span className="font-mono text-xs text-slate-600 truncate">{latest.cdnUrl}</span>
                  <button 
                    onClick={() => copy(latest.cdnUrl, "YouTube CDN URL copied")}
                    className="shrink-0 px-2.5 py-1 text-xs font-medium border border-slate-200 rounded hover:bg-slate-50 active:bg-slate-100 transition cursor-pointer"
                  >
                    Copy
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}